import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import AIButton from 'components/shared/ai/AIButton';
import filesColumns from 'components/shared/ai/summary/filesColumns';
import { getSummaryButtonTooltip } from 'components/shared/ai/summary/lib';
import Button from 'components/shared/Button';
import DataTable, { DataTableTheme } from 'components/shared/DataTable';
import Tooltip from 'components/shared/Tooltip';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import useIsMobile from 'hooks/useIsMobile';
import Attachment from 'models/Attachment';
import { PERMISSIONS } from 'models/Profile';
import { ReviewType } from 'models/Review';
import Authorization from 'models/reviews/Authorization';
import Referral from 'models/reviews/Referral';
import { indexAttachments } from 'services/api/attachments';
import { attachmentsQueryKeys } from 'services/api/attachments';
import {
  authorizationQueryKeys,
  authorizationQueryParams,
  referralQueryKeys,
  referralQueryParams,
  updateReview,
  UpdateReviewInput,
} from 'services/api/reviews';
import { BodySmallBold, Label } from 'styles/typography';

type ReviewModel = Authorization | Referral;

type Props = {
  locationEpisodeId?: string;
  review: ReviewModel;
  isSummarized?: boolean;
  files: Attachment[];
  canAddFiles?: boolean;
  canRemoveFiles?: boolean;
  onSummarizeFiles?: () => void;
};

export default function FilesTab({
  locationEpisodeId,
  review,
  files,
  canAddFiles = true,
  canRemoveFiles = true,
  isSummarized = false,
  onSummarizeFiles,
}: Props) {
  const isMobile = useIsMobile();

  const [addingFiles, setAddingFiles] = useState<boolean>(false);
  const [additionalFilesSelection, setAdditionalFilesSelection] = useState<Record<string, boolean>>({});

  const { profile } = useProfile();
  const { episode } = useEpisode();
  const { locationEpisode } = useSelectedLocationEpisode();

  // Keep a reference version of files so that we don't need a new callback function
  // each time we poll, because that would mean we'd need to rerender the table
  // each time we poll.
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const canCreateSummary = profile?.has(PERMISSIONS.aiJobSummaryCreate, { episode, locationEpisode }) ?? false;
  const isLocationEpisodeDischarged = locationEpisode?.discharged ?? false;

  const allAttachmentsFailedProcessing = files.every((attachment) => attachment.extractDataAIJob?.isErrored);

  const shouldDisableSummaryButton =
    allAttachmentsFailedProcessing || (!isSummarized && (!canCreateSummary || isLocationEpisodeDischarged));

  const shouldDisableFileManagement =
    isLocationEpisodeDischarged || !canCreateSummary || episode?.archived || locationEpisode?.archived;

  const summaryButtonTooltip = getSummaryButtonTooltip({
    reviewType: review.type,
    attachments: files,
    allAttachmentsFailedProcessing,
  });

  const isAuthorization = review.type === ReviewType.AUTHORIZATION;

  const queryClient = useQueryClient();

  const queryParams = useMemo(() => {
    return {
      ...(isAuthorization ? { locationEpisodeId } : { reviewId: review?.id }),
      'id.not': files.map((attachment) => attachment.id).join(','),
      pageSize: 10,
      archived: false,
      include: 'blob,createdByUser,createdByGroup,extractDataAIJob',
      sortBy: 'createdAt desc',
      viewOnly: true,
    };
  }, [isAuthorization, locationEpisodeId, review, files]);

  const query = useInfiniteQuery({
    queryKey: [review.type, ...attachmentsQueryKeys.index(queryParams)],
    queryFn: ({ pageParam, signal }) => indexAttachments({ ...queryParams, page: pageParam as number }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    select: (data) => {
      if (
        data.pages
          .flatMap((page) => page.data)
          .some((attachment) => !Object.keys(additionalFilesSelection).includes(attachment.id))
      ) {
        setAdditionalFilesSelection({
          ...data.pages
            .flatMap((page) => page.data)
            .reduce((acc, attachment) => ({ ...acc, [attachment.id]: false }), {}),
          ...additionalFilesSelection,
        });
      }
      return data;
    },
    enabled: !!(isAuthorization ? locationEpisodeId : review?.id) && addingFiles,
  });

  const reviewQueryKeys = isAuthorization ? authorizationQueryKeys : referralQueryKeys;
  const reviewQueryParams = isAuthorization ? authorizationQueryParams : referralQueryParams;

  const { mutate } = useMutation({
    mutationFn: (params: UpdateReviewInput) => updateReview(params),
    onSuccess: (data: ReviewModel) => {
      queryClient.setQueryData(reviewQueryKeys.show({ id: review.id }), data);
    },
  });

  const mutateReviewAttachments = useCallback(
    (attachmentIds: string[]) => {
      mutate({
        ...reviewQueryParams.show(review.id),
        attachmentIds,
      });
    },
    [mutate, reviewQueryParams, review.id]
  );

  const removeFile = useCallback(
    (removedFile: Attachment) => {
      mutateReviewAttachments(filesRef.current.filter((file) => file.id !== removedFile.id).map((file) => file.id));
    },
    [mutateReviewAttachments]
  );

  const closeAddFiles = useCallback(() => {
    setAddingFiles(false);
    setAdditionalFilesSelection({});
  }, []);

  const handleAddFiles = useCallback(() => {
    mutateReviewAttachments(
      [
        ...files,
        ...Object.entries(additionalFilesSelection)
          .filter(([_, value]) => value)
          .map(([key]) => ({ id: key })),
      ].map((file) => file.id)
    );

    closeAddFiles();
  }, [additionalFilesSelection, closeAddFiles, files, mutateReviewAttachments]);

  const handleSummarizeButtonClick = () => {
    onSummarizeFiles?.();
  };

  const columns = useMemo(
    () =>
      filesColumns({
        additionalFilesSelection,
        setAdditionalFilesSelection,
        removeFile,
        reviewId: review.id,
        reviewType: review.type,
      }),
    [additionalFilesSelection, setAdditionalFilesSelection, removeFile, review.id, review.type]
  );

  const anySelected = useMemo(
    () => Object.values(additionalFilesSelection).some((value) => value),
    [additionalFilesSelection]
  );

  const dataTableProps = addingFiles
    ? {
        theme: DataTableTheme.OPEN,
        data: query.data?.pages.flatMap((page) => page.data) ?? [],
        hasMore: query.hasNextPage,
        loading: query.isLoading || query.isFetchingNextPage,
        onBodyEndReached: () => {
          if (query.hasNextPage) {
            query.fetchNextPage();
          }
        },
        columnVisibility: {
          additionalFilesSelection: true,
          createdByUser: !isMobile,
          createdAt: !isMobile,
          delete: false,
        },
      }
    : {
        theme: DataTableTheme.DEFAULT,
        data: files.sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()),
        columnVisibility: {
          additionalFilesSelection: false,
          createdByUser: !isMobile,
          createdAt: !isMobile,
          delete: canRemoveFiles && !shouldDisableFileManagement,
        },
      };

  return (
    <Content>
      <Header>
        <HeaderLeft>
          <HeaderText>
            {addingFiles ? 'Add Additional Files' : isAuthorization ? 'Files for Review' : 'Referral Files'}
          </HeaderText>
          <HeaderSubtext>
            {addingFiles ? 'Select additional files to add as' : 'The files below have been provided as'}{' '}
            {isAuthorization ? 'clinical documentation for review' : 'referral documentation'}
          </HeaderSubtext>
        </HeaderLeft>
        <HeaderRight>
          {addingFiles ? (
            <>
              <AddAdditionalFilesButton variant='ghost' onClick={closeAddFiles}>
                Cancel
              </AddAdditionalFilesButton>
              <AddAdditionalFilesButton variant='primary' onClick={handleAddFiles} disabled={!anySelected}>
                Add Files
              </AddAdditionalFilesButton>
            </>
          ) : (
            <>
              {canAddFiles && (
                <AddAdditionalFilesButton
                  variant='outline-primary'
                  disabled={shouldDisableFileManagement}
                  onClick={() => setAddingFiles(true)}>
                  Add Additional Files
                </AddAdditionalFilesButton>
              )}
              <Tooltip text={summaryButtonTooltip} disabled={!shouldDisableSummaryButton || !summaryButtonTooltip}>
                <div>
                  <AIButton disabled={shouldDisableSummaryButton} onClick={handleSummarizeButtonClick}>
                    {isSummarized ? 'View Summary' : 'Summarize Files'}
                  </AIButton>
                </div>
              </Tooltip>
            </>
          )}
        </HeaderRight>
      </Header>
      <DataTable {...dataTableProps} maxHeight='none' columns={columns} hasFooter={false} />
    </Content>
  );
}

const HeaderText = styled(BodySmallBold)`
  line-height: 14px;
`;

const HeaderSubtext = styled(Label)`
  line-height: 12px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
`;

const AddAdditionalFilesButton = styled(Button)`
  font-size: 12px;
  padding: 8px 16px;
  height: 36px;
`;
